import React, { useMemo, useRef, useState } from "react";
import { Stack, Text, TextField, PrimaryButton, DefaultButton, Depths, Spinner, SpinnerSize, Panel, Image, PanelType } from "@fluentui/react";
import axios from "axios";
import { styles } from "../pages/ProposalDashboard";

const TYPICAL_TURN = { inputTokens: 2500, outputTokens: 600 }; // adjust for your approximate usage pattern

export const OPENAI_CHAT_MODELS = [
  {
    id: "gpt-4o-mini",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-4o-mini.png",
    pricingPer1MTokens: { input: 0.15, cachedInput: 0.075, output: 0.60 },
    contextWindow: 128000,
    maxOutputTokens: 16384,
    speedTier: "fast",
    bestFor: ["high-volume chat", "RAG chat", "summaries", "classification"],
  },
  {
    id: "gpt-4o",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-4o.png",
    pricingPer1MTokens: { input: 2.50, cachedInput: 1.25, output: 10.00 },
    contextWindow: 128000,
    maxOutputTokens: 16384,
    speedTier: "fast",
    bestFor: ["strong general chat", "tool calling", "structured outputs"],
  },

  {
    id: "gpt-4.1",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-4.1.png",
    pricingPer1MTokens: { input: 2.00, cachedInput: 0.50, output: 8.00 },
    contextWindow: 1047576,
    maxOutputTokens: 32768,
    speedTier: "medium",
    bestFor: ["very long-context chat", "agents", "large RAG prompts"],
  },
  {
    id: "gpt-4.1-mini",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-4.1-mini.png",
    pricingPer1MTokens: { input: 0.40, cachedInput: 0.10, output: 1.60 },
    contextWindow: 1047576,
    maxOutputTokens: 32768,
    speedTier: "fast",
    bestFor: ["cheap long-context", "fast tool calling", "large RAG prompts"],
  },
  {
    id: "gpt-4.1-nano",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-4.1-nano.png",
    pricingPer1MTokens: { input: 0.10, cachedInput: 0.025, output: 0.40 },
    contextWindow: 1047576,
    maxOutputTokens: 32768,
    speedTier: "fastest",
    bestFor: ["routing", "classification", "ultra-cheap long-context"],
  },

  {
    id: "o3-mini",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/o3-mini.png",
    pricingPer1MTokens: { input: 1.10, cachedInput: 0.55, output: 4.40 },
    contextWindow: 200000,
    maxOutputTokens: 100000,
    speedTier: "medium",
    bestFor: ["reasoning-heavy tasks", "hard RAG synthesis", "structured extraction"],
  },
  {
    id: "o3",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/o3.png",
    pricingPer1MTokens: { input: 2.00, cachedInput: 0.50, output: 8.00 },
    contextWindow: 200000,
    maxOutputTokens: null,
    speedTier: "medium",
    bestFor: ["higher-quality reasoning", "complex synthesis"],
  },
  {
    id: "o3-pro",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/o3-pro.png",
    pricingPer1MTokens: { input: 20.00, output: 80.00 },
    contextWindow: 200000,
    maxOutputTokens: 100000,
    speedTier: "slow",
    bestFor: ["highest-stakes reasoning"],
  },

  {
    id: "gpt-5.1",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-5.1.png",
    pricingPer1MTokens: { input: 1.25, cachedInput: 0.125, output: 10.00 },
    contextWindow: 400000,
    maxOutputTokens: 128000,
    speedTier: "medium",
    bestFor: ["agentic workflows", "coding + reasoning", "planning"],
  },
  {
    id: "gpt-5-mini",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-5-mini.png",
    pricingPer1MTokens: { input: 0.25, cachedInput: 0.025, output: 2.00 },
    contextWindow: 400000,
    maxOutputTokens: 128000,
    speedTier: "fast",
    bestFor: ["cost-sensitive agentic flows", "RAG synthesis at scale"],
  },
  {
    id: "gpt-5-nano",
    iconPng: "https://cdn.openai.com/API/docs/images/model-page/model-icons/gpt-5-nano.png",
    pricingPer1MTokens: { input: 0.05, cachedInput: 0.005, output: 0.40 },
    contextWindow: 400000,
    maxOutputTokens: 128000,
    speedTier: "fastest",
    bestFor: ["summarization", "classification", "cheap routing"],
  },
].map((m) => {
  const { blendedPer1M, costTier, disabled } = computeCostTier(m.pricingPer1MTokens);
  const exampleTurnCostUSD = estimateTurnCostUSD({
    inputTokens: TYPICAL_TURN.inputTokens,
    outputTokens: TYPICAL_TURN.outputTokens,
    pricingPer1MTokens: m.pricingPer1MTokens,
  });

  return {
    ...m,
    costTier,  
    disabled,                    // "Cheap", "Moderate", etc.
    blendedPricePer1M: blendedPer1M, // numeric, useful for sorting
    exampleTurn: {
      inputTokens: TYPICAL_TURN.inputTokens,
      outputTokens: TYPICAL_TURN.outputTokens,
      estimatedCostUSD: exampleTurnCostUSD,
    },
  };
});

function computeCostTier(pricingPer1MTokens) {
  const input = pricingPer1MTokens?.input ?? 0;
  const output = pricingPer1MTokens?.output ?? 0;

  // Weighted blend: typical chat turn is input-heavy (history + chunks)
  const blendedPer1M = 0.7 * input + 0.3 * output;

  let costTier = "Unknown";
  let disabled = false;
  if (blendedPer1M < 0.20) {
    disabled = false;
    costTier = "Ultra Cheap";
  }
  else if (blendedPer1M < 0.60)  {
    disabled = false;
    costTier = "Cheap";
  }
  else if (blendedPer1M < 2.00) {
    disabled = true;
    costTier = "Moderate";
  }
  else if (blendedPer1M < 8.00) {
    disabled = true;
    costTier = "Expensive";
  }
  else {
    disabled = true;
    costTier = "Very Expensive";
  };

  return { blendedPer1M, costTier, disabled };
}

function estimateTurnCostUSD({ inputTokens, outputTokens, pricingPer1MTokens }) {
  const inCost = (inputTokens / 1_000_000) * (pricingPer1MTokens?.input ?? 0);
  const outCost = (outputTokens / 1_000_000) * (pricingPer1MTokens?.output ?? 0);
  return inCost + outCost;
}


export default function ChatInterface({
  selectedChunks,
  characterModeEnabled,
  characterPrompt,
  chatTitle="Chat"
}) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ask me questions based only on the selected chunks." },
  ]);
  const [draftRequest, setDraftRequest] = useState(""); // optional: for “formal output” requests
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [tokenTotals, setTokenTotals] = useState({ input: 0, output: 0, total: 0 });
  const [selectedModel, setSelectedModel] = useState("gpt-4.1-nano");
  const [showSelectModelPanel, toggleShowSelectModelPanel] = useState(false);

  const scrollRef = useRef(null);

  const canChat = useMemo(
    () => Array.isArray(selectedChunks) && selectedChunks.length > 0,
    [selectedChunks]
  );

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  };

  const estimatedCostofChat = useMemo(() => {
    const model = OPENAI_CHAT_MODELS.find((m) => m.id === selectedModel);
    if (!model) return 0;
    return estimateTurnCostUSD({
      inputTokens: tokenTotals.input,
      outputTokens: tokenTotals.output,
      pricingPer1MTokens: model.pricingPer1MTokens,
    });
  }, [selectedModel, tokenTotals]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;

    if (!canChat) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Select at least one chunk first." },
      ]);
      setInput("");
      return;
    }

    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    scrollToBottom();

    try {
      const resp = await axios.post("/api/chat-with-chunks", {
        messages: nextMessages,
        chunks: selectedChunks,
        characterModeEnabled: !!characterModeEnabled,
        characterPrompt: characterPrompt || "",
        selectedModel
      });

      const reply = resp.data?.reply || "(No reply)";
      const usage = resp.data?.usage || {};
      setTokenTotals({
        input: (usage.input_tokens || 0) + tokenTotals.input,
        output: (usage.output_tokens || 0) + tokenTotals.output,
        total: (usage.total_tokens || 0) + tokenTotals.total,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Chat failed";
      setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${msg}` }]);
    } finally {
      setSending(false);
      scrollToBottom();
    }
  };

  const clearChat = () => {
    setMessages([{ role: "assistant", content: "Ask me questions based only on the selected chunks." }]);
  };

  return (
    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { height: "100%", minHeight: 480 } }}>
      {/* Model Selection Panel */}
      <Panel type={PanelType.medium} isOpen={showSelectModelPanel} onDismiss={() => toggleShowSelectModelPanel(false)} headerText="Select Chat Model">
        <Stack horizontal wrap tokens={{ childrenGap: 12 }} verticalAlign="center">
          {OPENAI_CHAT_MODELS.map((model) => (
            <Stack grow={1} key={model.id} tokens={{childrenGap: 16}} horizontal styles={{root: {border: model.id === selectedModel ? "2px solid #0078d4" : "2px solid #f0f0f0", borderRadius: 6, padding: 12, cursor: model.disabled ? "default" : "pointer", boxShadow: Depths.depth4, backgroundColor: model.disabled ? "#f0f0f0" : "#ffffff"}}} onClick={() => {
              if (!model.disabled) setSelectedModel(model.id);
              }}>
              <Image src={model.iconPng} alt={model.id} width={64} height={64} />
              <Stack>
                <Text variant="medium" styles={{root: {fontWeight: 600}}}>{model.id}</Text>
                <Text variant="small">Context Window: {model.contextWindow.toLocaleString()} tokens</Text> 
                <Text variant="small">Cost Tier: {model.costTier}</Text>
                <Text variant="small">Example Turn Cost: ${model.exampleTurn.estimatedCostUSD.toFixed(4)}</Text>
                <Text variant="small">Best For: {model.bestFor.join(", ")}</Text>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Panel>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack>
          <Text variant="xLarge">{chatTitle}</Text>
          <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
            {canChat ? `Grounded in ${selectedChunks.length} selected chunk(s)` : "Select chunks to enable grounded chat."}
          </Text>
          {characterModeEnabled ? (
            <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
              Character mode: ON (chat tone only)
            </Text>
          ) : null}
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton text={`Model: ${selectedModel}`} onClick={() => toggleShowSelectModelPanel(true)} />
          <DefaultButton text="Clear" onClick={clearChat} />
        </Stack>
      </Stack>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          border: "1px solid #e1dfdd",
          borderRadius: 6,
          padding: 12,
          background: "#faf9f8",
          boxShadow: Depths.depth4
        }}
      >
        <Stack tokens={{ childrenGap: 10 }}>
          {messages.map((m, i) => {
            const isUser = m.role === "user";
            return (
              <Stack key={i} horizontal horizontalAlign={isUser ? "end" : "start"}>
                <div
                  style={{
                    maxWidth: "78%",
                    padding: "8px 10px",
                    borderRadius: 8,
                    background: isUser ? "#0078d4" : "#ffffff",
                    color: isUser ? "#ffffff" : "#323130",
                    border: isUser ? "none" : "1px solid #edebe9",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.45,
                    boxShadow: Depths.depth8
                  }}
                >
                  <Text styles={{ root: { color: "inherit" } }}>{m.content}</Text>
                </div>
              </Stack>
            );
          })}

          {sending && (
            <Stack horizontal horizontalAlign="start">
              <div
                style={{
                  maxWidth: "78%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "#ffffff",
                  border: "1px solid #edebe9",
                  color: "#605e5c",
                }}
              >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                  <Spinner size={SpinnerSize.small} />
                  <Text>Thinking…</Text>
                </Stack>
              </div>
            </Stack>
          )}
        </Stack>
      </div>

      {/* Composer */}
      <Stack horizontal tokens={{ childrenGap: 8 }} styles={{root: styles.sectionCard}} verticalAlign="end">
        <TextField
          placeholder={canChat ? "Type a message… (Enter to send, Shift+Enter for newline)" : "Select chunks first…"}
          disabled={!canChat || sending}
          multiline
          rows={2}
          value={input}
          onChange={(_, v) => setInput(v || "")}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          styles={{ root: { flex: 1 } }}
        />
        <PrimaryButton text="Send" onClick={send} disabled={!canChat || sending || !input.trim()} />
      </Stack>
      <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
        Tokens used this chat: {tokenTotals.total} (in {tokenTotals.input}, out {tokenTotals.output}): Estimated cost: ${estimatedCostofChat.toFixed(4)}
      </Text>

      {/* (Optional) Tip about formal vs chat */}
      {/* <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
        Tip: If you need copy-ready content, ask for a “Formal Draft” explicitly. Character mode will not apply to that section.
      </Text> */}
    </Stack>
  );
}
